import { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { fileToBase64, uploadImageWithUuid } from '../lib/storage';
import { analyzeClothingImage, ClothingItem } from '../lib/openai';
import { Tables } from '../lib/supabase';
import { SegmentationResult } from '../lib/segmentation/index';

export type WardrobeItem = Tables['wardrobe_items']['Row'];

export interface SegmentationMetadata {
  imageId?: string;
  maskId?: string | null;
  segmented?: boolean;
  maskUrl?: string | null;
}

export function useWardrobeItems(userId: string | undefined) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<WardrobeItem[]>([]);
  // Use a ref to track if an operation is cancelled to avoid state updates
  const cancelledRef = useRef(false);

  // Add a new item to wardrobe
  const addItem = async (
    imageFile: File, 
    segmentationData?: SegmentationMetadata
  ): Promise<WardrobeItem | null> => {
    if (!userId) {
      setError('User not authenticated');
      return null;
    }

    setLoading(true);
    setError(null);
    // Reset the cancellation state
    cancelledRef.current = false;

    try {
      // Convert the image to base64
      let base64Image = "";
      try {
        base64Image = await fileToBase64(imageFile);
      } catch (err) {
        console.error('Error converting image to base64:', err);
        setError('Unable to process the image. Please try again with a different image.');
        setLoading(false);
        return null;
      }
      
      // Check if cancelled during base64 conversion
      if (cancelledRef.current) return null;
      
      // Analyze the image with OpenAI
      let clothingItems: ClothingItem[] = [];
      try {
        clothingItems = await analyzeClothingImage(base64Image);
      } catch (err) {
        console.error('Error analyzing image with OpenAI:', err);
        setError('Unable to analyze the image. Please make sure it contains a clothing item.');
        setLoading(false);
        return null;
      }
      
      // Check if cancelled during analysis
      if (cancelledRef.current) return null;
      
      if (!clothingItems.length) {
        if (!cancelledRef.current) {
          setError('No clothing items detected in the image. Please try again with a clearer image of a clothing item.');
        }
        setLoading(false);
        return null;
      }
      
      // Use the first detected item for now (we can add multi-item support later)
      const detectedItem = clothingItems[0];
      
      // Extract the file extension
      const fileExt = imageFile.name.split('.').pop()?.toLowerCase() || 'jpg';
      
      // Determine if we have mask data from segmentation
      const hasMask = segmentationData?.segmented && segmentationData?.maskId;
      
      // Get mask base64 if available (from data URL)
      let base64Mask: string | null = null;
      if (hasMask && segmentationData?.maskId) {
        try {
          // If the mask URL is available through segmentation result, extract the base64
          if (segmentationData.maskUrl) {
            base64Mask = await fileToBase64(segmentationData.maskUrl as string);
          } else {
            console.warn('No mask URL available despite having maskId:', segmentationData.maskId);
          }
        } catch (err) {
          console.error('Error processing mask image:', err);
          // Continue without the mask if there's an error
          base64Mask = null;
        }
      }
      
      // Check if cancelled during mask processing
      if (cancelledRef.current) return null;
      
      // Upload image with UUID format
      let uploadResult;
      try {
        uploadResult = await uploadImageWithUuid(
          userId,
          base64Image,
          fileExt,
          base64Mask,
          segmentationData?.imageId, // Pass existing image ID if available
          segmentationData?.maskId    // Pass existing mask ID if available
        );
      } catch (err) {
        console.error('Error uploading image to storage:', err);
        setError('Failed to upload the image. Please try again.');
        setLoading(false);
        return null;
      }
      
      // Check if cancelled during upload
      if (cancelledRef.current) return null;
      
      // Create a wardrobe item in the database with references to both images
      const { data, error } = await supabase
        .from('wardrobe_items')
        .insert({
          user_id: userId,
          name: `${detectedItem.color} ${detectedItem.type}`,
          type: detectedItem.type,
          color: detectedItem.color,
          pattern: detectedItem.pattern || null,
          style: detectedItem.style || null,
          season: detectedItem.season || null,
          image_url: uploadResult.imageUrl,
          mask_url: uploadResult.maskUrl,
          image_id: uploadResult.imageId,
          mask_id: uploadResult.maskId,
          has_mask: !!uploadResult.maskUrl,
        })
        .select()
        .single();
      
      if (error) {
        console.error('Database error:', error);
        throw new Error(`Database error: ${error.message}`);
      }
      
      // Only update state if not cancelled
      if (!cancelledRef.current) {
        // Update the local state
        setItems((prev) => [...prev, data]);
      }
      
      return data;
    } catch (err) {
      // Only set error if not cancelled
      if (!cancelledRef.current) {
        console.error('Error adding wardrobe item:', err);
        setError(err instanceof Error ? err.message : 'Failed to add item to wardrobe');
      }
      return null;
    } finally {
      // Always clean up loading state
      setLoading(false);
    }
  };

  // Call this to cancel the current operation
  const cancelOperation = () => {
    cancelledRef.current = true;
  };

  // Get all wardrobe items for the current user
  const getItems = async (): Promise<WardrobeItem[]> => {
    if (!userId) {
      setError('User not authenticated');
      return [];
    }

    setLoading(true);
    setError(null);
    cancelledRef.current = false;

    try {
      const { data, error } = await supabase
        .from('wardrobe_items')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      if (!cancelledRef.current) {
        setItems(data);
      }
      
      return data;
    } catch (err) {
      if (!cancelledRef.current) {
        console.error('Error fetching wardrobe items:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch wardrobe items');
      }
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Delete a wardrobe item
  const deleteItem = async (itemId: string): Promise<boolean> => {
    if (!userId) {
      setError('User not authenticated');
      return false;
    }

    setLoading(true);
    setError(null);
    cancelledRef.current = false;

    try {
      const { error } = await supabase
        .from('wardrobe_items')
        .delete()
        .eq('id', itemId)
        .eq('user_id', userId);
      
      if (error) {
        throw error;
      }
      
      if (!cancelledRef.current) {
        // Update the local state
        setItems((prev) => prev.filter(item => item.id !== itemId));
      }
      
      return true;
    } catch (err) {
      if (!cancelledRef.current) {
        console.error('Error deleting wardrobe item:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete wardrobe item');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    items,
    loading,
    error,
    addItem,
    getItems,
    deleteItem,
    cancelOperation
  };
} 